import { Component, EventEmitter, Injector, Input, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { PatientAdapter } from 'app/core/adapters/patient.adapter';
import { PaymentService } from 'app/core/http/services/payment.service';
import { Encounter } from 'app/core/interfaces/encounter';
import { Patient } from 'app/core/interfaces/patient';
import { Payment } from 'app/core/interfaces/payment';
import { BaseComponent } from 'app/modules/shared/components/base.component';
import { pixCpfValidator } from 'app/core/validators/pixCpfValidator';
import { getHttpErrorMessage } from 'app/core/utils/http-error-message';

@Component({
  selector: 'src-payment-pix',
  templateUrl: './payment-pix.component.html',
  styleUrls: ['./payment-pix.component.css'],
})
export class PaymentPixComponent extends BaseComponent {
  customerInfoForm: FormGroup;

  @Output() onConfirmPayment = new EventEmitter<Payment>();

  @Input() encounter!: Encounter;
  @Input() forceNew = false;

  public submitted = false;
  public patientProfile!: Patient;
  public paymentErrorMessage = 'Falha ao gerar o Qrcode. Confira seus dados e tente novamente!';

  constructor(
    private injector: Injector,
    private fb: FormBuilder,
    private paymentService: PaymentService,
    private patientAdapter: PatientAdapter,
  ) {
    super(injector);
    this.customerInfoForm = this.fb.group({
      name: ['', Validators.required],
      cpfCnpj: ['', [Validators.required, pixCpfValidator]],
      phone: ['', [Validators.required, Validators.pattern('^[0-9]{10,11}$')]],
      email: ['', [Validators.required, Validators.email]],
    });
  }

  ngOnInit() {
    this.patientProfile = this.getPaymentPatient();

    this.customerInfoForm.patchValue({
      name: this.patientProfile.name,
      cpfCnpj: this.onlyNumbers(this.patientProfile.cpf),
      phone: this.normalizePhone(this.patientProfile.phone || ''),
      email: this.patientProfile.email,
    });
  }

  createPayment() {
    if (this.customerInfoForm.invalid) {
      this.customerInfoForm.markAllAsTouched();
      return;
    }

    const customerFormValue = this.customerInfoForm.value;
    const encounterId = this.encounter.encounter_id || '';
    customerFormValue.patient_id = this.patientProfile.patient_id;
    customerFormValue.birthDate = this.patientProfile.birthDate;
    customerFormValue.cpfCnpj = this.onlyNumbers(customerFormValue.cpfCnpj);
    customerFormValue.phone = this.normalizePhone(customerFormValue.phone);

    this.submitted = true;

    const payment: Payment = {
      encounter_id: encounterId,
      customer_info: customerFormValue,
      payment_method: 'PIX',
      force_new: this.forceNew,
    };

    this.paymentService.create(payment).subscribe({
      next: (payment: Payment) => {
        this.showSuccessMessage = true;
        this.submitted = false;
        this.onConfirmPayment.emit(payment);
      },
      error: (err) => {
        this.paymentErrorMessage = getHttpErrorMessage(err, 'Falha ao gerar o Qrcode. Confira seus dados e tente novamente!');
        this.showErrorMessage = true;
        this.submitted = false;
      },
    });
  }

  canGenerateQrCode(): boolean {
    return !this.submitted && !this.showSuccessMessage && this.customerInfoForm.valid;
  }

  shouldShowInvalidDocumentMessage(): boolean {
    const documentControl = this.customerInfoForm.get('cpfCnpj');

    return this.isFullDocumentTyped() && !!documentControl?.hasError('documentInvalid');
  }

  isFullDocumentTyped(): boolean {
    const documentLength = this.onlyNumbers(this.customerInfoForm.get('cpfCnpj')?.value).length;

    return documentLength === 11 || documentLength === 14;
  }

  getInvalidDocumentMessage(): string {
    const documentLength = this.onlyNumbers(this.customerInfoForm.get('cpfCnpj')?.value).length;

    return documentLength === 14 ? 'CNPJ informado não é válido' : 'CPF informado não é válido';
  }

  private onlyNumbers(value: string): string {
    return String(value || '').replace(/\D/g, '');
  }

  private normalizePhone(value: string): string {
    const phone = this.onlyNumbers(value);

    if (phone.startsWith('55') && phone.length > 11) {
      return phone.substring(2);
    }

    return phone;
  }

  private getPaymentPatient(): Patient {
    if (this.currentUser?.type === 'patient') {
      return this.patientAdapter.adapterToModel(this.currentUser.user);
    }

    const patientDetails = this.encounter?.patient_details || {};
    const patient: any = this.encounter?.patient || {};

    return {
      patient_id: patientDetails.patient_id || patient.patient_id || '',
      name: patientDetails.name || patient.name || '',
      gender: patientDetails.gender || 'Male',
      birthDate: patientDetails.birthDate || patient.birthDate || '',
      cpf: patientDetails.cpf || patientDetails.cpfCnpj || patientDetails.document || patient.cpf || '',
      mobile: patientDetails.mobile || patientDetails.phone || patient.phone || '',
      phone: patientDetails.phone || patientDetails.mobile || patient.phone || '',
      email: patientDetails.email || patient.email || '',
      active: true,
      address: patientDetails.address || {
        cep: '',
        number: '',
      },
    } as Patient;
  }
}
